<script setup lang="ts">
import ColorPickerInput from "@/@core/components/app-form-elements/ColorPickerInput.vue";
import { formatPrice, textTruncate } from "@/utils/Formatting";
import { fetchAndSetFile, makeImageHandler } from "@/utils/ImageUtil";
import { pb } from "@/utils/PocketBaseAdapter";
import { useProductStore } from "@/utils/ProductStore";
import { decimalRule, fileSizeValidator, numberValidator, requiredValidator } from "@validators";
import { VForm } from "vuetify/components/VForm";

const productStore = useProductStore();

const ticket = defineModel<any>({ required: true });

const emit = defineEmits<{
  remove: [];
}>();

const isOpen = ref(ticket.value.id ? false : true);
const formRef = ref<InstanceType<typeof VForm>>();

defineExpose({
  validate: async () => {
    const result = await formRef.value?.validate();
    if (!result?.valid) isOpen.value = true;
    return result;
  },
});
const productImageFile = ref<any>();
const originalFiles: Record<string, File> = {};
const productImagePreview = ref<any>(null);

const btnTextSwatches = [
  { name: "Default", value: "" },
  { name: "White", value: "#FFFFFF" },
  { name: "Black", value: "#000000" },
  { name: "Lightgray", value: "#D3D3D3" },
  { name: "Darkgray", value: "#636363" },
];


const handleImageChange = makeImageHandler(productImageFile, productImagePreview);
watch(productImageFile, (val) => {
  if (!val) productImagePreview.value = null;
  ticket.value.productImage = val ?? undefined;
});

fetchAndSetFile("productImage", ticket.value, productImageFile, productImagePreview, originalFiles)
</script>

<template>
  <div class="mb-4 border rounded-sm px-2">
    <!-- Collapsible header -->
    <div
      class="d-flex align-center justify-space-between px-2 py-2 rounded cursor-pointer ticket-header"
      @click="isOpen = !isOpen"
    >
      <span class="font-weight-medium">
        {{ ticket.name || "New Ticket" }}
      </span>
      <div>
        <IconBtn @click="emit('remove')">
          <VIcon size="20" icon="mdi-delete" />
          <VTooltip activator="parent" open-delay="400">
            Remove
          </VTooltip>
        </IconBtn>
        <VIcon :icon="isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'" />
      </div>
    </div>

    <VForm ref="formRef">
    <div v-show="isOpen" class="pt-2 pa-3 px-2">
      <!-- Row 1: Name, Sell Price, Sort Order -->
      <VRow>
        <VCol sm="4" cols="12">
          <VTextField
            v-model="ticket.name"
            :rules="[requiredValidator]"
            label="Name"
            required
          />
        </VCol>
        <VCol sm="4" cols="6">
          <VTextField
            v-model="ticket.sellPrice"
            :rules="[requiredValidator, numberValidator, decimalRule]"
            prefix="$"
            label="Sell Price"
            :formatter="formatPrice"
          />
        </VCol>
        <VCol sm="4" cols="6">
          <VTextField
            v-model="ticket.sortOrder"
            :rules="[numberValidator]"
            label="Sort Order"
            required
            type="number"
          />
          <VTooltip activator="parent" open-delay="1000" scroll-strategy="close">
            <span>
              The order that the Buttons<br />
              will show on the POS screen <br />
              lower number is first.<br />
              Can be a Negitive Number
            </span>
          </VTooltip>
        </VCol>
      </VRow>

      <!-- Row 2: Bar Value, Ticket Count, Ticket Limit -->
      <VRow>
        <VCol sm="4" cols="6">
          <VTextField
            v-model="ticket.eventBarValue"
            :rules="[requiredValidator]"
            type="number"
            label="Bar Value"
          />
        </VCol>
        <VCol sm="4" cols="6">
          <VTextField
            v-model="ticket.eventTicketLimit"
            :rules="[requiredValidator, numberValidator]"
            label="Ticket Count"
            placeholder="Ticket Count"
          />
          <VTooltip activator="parent" open-delay="1000" scroll-strategy="close">
            <span>
              The Number of this type of <br />
              Ticket that can be sold
            </span>
          </VTooltip>
        </VCol>
        <VCol sm="4" cols="6">
          <VTextField
            v-model="ticket.maxDiscount"
            :rules="[numberValidator]"
            suffix="%"
            label="Max Discount"
            required
            type="number"
          />
          <VTooltip activator="parent" open-delay="1000" scroll-strategy="close">
            <span>
              The Maximium Discount<br />
              That can be given on <br />
              This Product
            </span>
          </VTooltip>
        </VCol>
      </VRow>

      <VRow>
        <VCol cols="12" sm="6">
          <VTextarea
            v-model="ticket.description"
            rows="3"
            label="Description"
          />
        </VCol>
        <VCol sm="6" cols="12">
          <VSelect
            v-model="ticket.reportingGroup"
            :items="productStore.reportingGroupList"
            item-title="name"
            item-value="id"
            return-object
            label="Reporting Group"
            clearable
            messages="If set, ticket reporting group takes precedence otherwise category reporting group will be used"
          />
        </VCol>
      </VRow>

      <!-- Row 4: Sales End, Checkboxes -->
      <VRow>
        <VCol sm="6" cols="12">
          <DateInput
            v-model="ticket.salesEnd"
            label="Sales End"
            show-time
            format="DD MMM YYYY hh:mma"
            clearable
          />
        </VCol>
      </VRow>
      <VRow>
        <VCol sm="3" cols="6">
          <VCheckbox
            v-model="ticket.canBuyInWebshop"
            label="Show in Webshop"
          />
        </VCol>
        <VCol sm="3" cols="6">
          <VCheckbox
            v-model="ticket.canBuyInKiosk"
            label="Show in Kiosk"
          />
        </VCol>
        <VCol sm="3" cols="6">
          <VCheckbox
            v-model="ticket.canPayInStore"
            label="Can Pay In Store"
          />
        </VCol>
      </VRow>

      <!-- Row 5: Color/Image section -->
      <VRow v-if="!ticket.notDirectlySold">
        <VCol sm="3" cols="12" class="d-flex justify-center flex-column gap-2">
          <VCard
            :height="100"
            :width="180"
            :color="`${ticket.btnBackColor}`"
            class="d-flex flex-column"
            elevation="8"
          >
            <VTooltip activator="parent" location="top">
              {{
                ticket.description
                  ? ticket.description
                  : "Product Description & Sell Price"
              }}
            </VTooltip>
            <VCardTitle
              class="text-wrap wrapped-text"
              :style="{ color: ticket.btnTextColor }"
            >
              {{
                ticket.name
                  ? textTruncate(ticket.name)
                  : "Description"
              }}
            </VCardTitle>
            <VCardSubtitle
              class="d-flex flex-row-reverse flex-grow-1 align-end"
              :style="{ color: ticket.btnTextColor }"
            >
              {{
                ticket.sellPrice
                  ? formatPrice(ticket.sellPrice)
                  : "$ 0.00"
              }}
            </VCardSubtitle>
          </VCard>
        </VCol>
        <VCol sm="3" cols="12" class="d-flex flex-column gap-2">
          <div>
            <VSelect
              v-model="ticket.btnTextColor"
              :items="btnTextSwatches"
              density="compact"
              item-title="name"
              label="Text Color"
            >
              <template v-slot:item="{ props }">
                <VListItem v-bind="props"></VListItem>
              </template>
            </VSelect>
          </div>
          <ColorPickerInput
            :color="ticket.btnBackColor"
            label="Background Color"
            @updateColor="ticket.btnBackColor = $event"
          />
        </VCol>

        <!-- Image upload input -->
        <VCol cols="12" sm="6">
          <div class="d-flex align-center">
            <VFileInput
              v-model="ticket.productImage"
              label="Image"
              placeholder="Upload your Image"
              accept="image/png, image/jpeg, image/bmp"
              prepend-icon=""
              clearable
              class="flex-grow-1"
              :rules="[v => fileSizeValidator(v, 500)]"
              @change="handleImageChange"
            />
            <VAvatar v-if="productImagePreview" :image="productImagePreview" size="x-large" class="ms-3 rounded-sm" />
          </div>
        </VCol>
      </VRow>
    </div>
    </VForm>
  </div>
</template>
